// @file Helper method to generate payloads for native links

import {
  isBreakoutLinkUrlFormat,
  isProfileUrlFormat,
  isSlideshowUrlFormat,
  isSubmissionRequestUrlFormat,
  isWallOrWallWishUrlFormat,
} from '@@/bits/url'
import { NativeAppOpenLink } from '@@/pinia/native_app'

/**
 * Generates the payload for internal links to be opened in the native app.
 *
 * @param link - The URL of the internal link
 * @returns An object containing the link type, content category, and subcategory (if applicable)
 *
 * NOTE: When adding new cases for handling URLs, the order of checks should be based on the specificity of the path.
 * If a new link type is a subset of an existing regex check, it must be placed before the corresponding conditional.
 * This ensures that more specific cases are handled before more general ones.
 */
const getNativeLinkPayloadForInternalLink = (link: string): Omit<NativeAppOpenLink, 'url'> => {
  if (isSlideshowUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'slideshow' }
  }
  if (isWallOrWallWishUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'padlet' }
  }
  if (isProfileUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'profile' }
  }
  if (isSubmissionRequestUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'submission-request' }
  }
  if (isBreakoutLinkUrlFormat(link)) {
    return { linkType: 'internal', contentCategory: 'page', contentSubcategory: 'breakout-link' }
  }
  // could be dashboard internal links or other subcategories that we don't handle in the native app yet
  return { linkType: 'internal', contentCategory: 'page' }
}

export { getNativeLinkPayloadForInternalLink }
